"use client";

import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import gsap from "gsap";

/* ─── Context shape ─── */
interface TransitionCtx {
  transitionTo: (href: string) => void;
  isTransitioning: boolean;
}

const Ctx = createContext<TransitionCtx>({
  transitionTo: () => {},
  isTransitioning: false,
});

export const useTransition = () => useContext(Ctx);

/* ─── Provider ─── */
export function TransitionProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pendingRef = useRef<string | null>(null);
  const prevPathRef = useRef(pathname);

  /* ── Trigger a page transition ── */
  const transitionTo = useCallback(
    (href: string) => {
      // Guard: skip if already transitioning, same page, or external
      if (isTransitioning) return;
      if (href === pathname) return;

      setIsTransitioning(true);
      setIsActive(true);
      pendingRef.current = href;

      // Wait one frame for overlay to mount, then animate in
      requestAnimationFrame(() => {
        if (!overlayRef.current) return;
        gsap.fromTo(
          overlayRef.current,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: 0.35,
            ease: "power2.inOut",
            onComplete: () => {
              if (pendingRef.current) {
                router.push(pendingRef.current);
              }
            },
          }
        );
      });
    },
    [isTransitioning, pathname, router]
  );

  /* ── Detect route change → fade overlay out ── */
  useEffect(() => {
    if (pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;

    if (!isTransitioning || !overlayRef.current) return;

    // Small delay to let the new page paint
    const timer = setTimeout(() => {
      if (!overlayRef.current) return;
      gsap.to(overlayRef.current, {
        autoAlpha: 0,
        duration: 0.5,
        ease: "power2.out",
        onComplete: () => {
          setIsTransitioning(false);
          setIsActive(false);
          pendingRef.current = null;
        },
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [pathname, isTransitioning]);

  return (
    <Ctx.Provider value={{ transitionTo, isTransitioning }}>
      {children}

      {/* ── Transition overlay ── */}
      {isActive && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-[99998] flex items-center justify-center pointer-events-none"
          style={{ visibility: "hidden", opacity: 0 }}
        >
          {/* Background */}
          <div className="absolute inset-0 bg-[#050d14]" />

          {/* Radial glow */}
          <div
            className="absolute w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(249,115,22,0.08) 40%, transparent 70%)",
              filter: "blur(50px)",
            }}
          />

          {/* Logo mark */}
          <div className="relative z-10 flex flex-col items-center gap-3">
            <span
              className="text-2xl font-black tracking-widest uppercase"
              style={{
                fontFamily: "var(--font-orbitron), sans-serif",
                background:
                  "linear-gradient(135deg, #f97316 0%, #8b5cf6 45%, #2016f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              MONT2 ACADEMY
            </span>
            {/* Pulse dot */}
            <span className="block h-1.5 w-1.5 rounded-full bg-[#8b5cf6] animate-pulse shadow-[0_0_12px_rgba(139,92,246,0.6)]" />
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
